import { useChat } from '@ai-sdk/react';
import { AgentId } from '@procureai/agents';
import { DefaultChatTransport } from 'ai';
import { isNil } from 'ramda';
import { useState } from 'react';
import { Union } from 'ts-toolbelt';
import { v4 as uuidV4, v7 as uuidv7 } from 'uuid';

import { SvbrgConversationEntityVariant } from '@/generated/gql/graphql';
import { UIChatMessage } from '@/legacy/components/Chat/chat.types';
import { fetchClient } from '@/legacy/services/http';
import { appConfig } from '~/common/config';
import { useChatHistoryHydration } from '~/hooks/useChatHistoryHydration/useChatHistoryHydration';

import { ConversationStatus, UseConversationResult } from './useConversation.types';
import { getConversationStatus } from './useConversation.utils';

export interface UseConversationProps {
  entityId: string;
  entityVariant: SvbrgConversationEntityVariant;
  conversationId: Union.Nullable<string>;
  messageHistory: UIChatMessage[];
  loading?: boolean;
}
export const useConversation = ({
  entityId,
  entityVariant,
  conversationId,
  messageHistory,
  loading,
}: UseConversationProps): UseConversationResult => {
  const [internalStreamId, setInternalStreamId] = useState<string>(uuidV4());

  // initialize the chat SDK with empty messages and update once the data is loaded
  const {
    sendMessage,
    setMessages,
    messages,
    status: chatSdkStatus,
    error,
  } = useChat<UIChatMessage>({
    id: internalStreamId,
    generateId: uuidv7,
    transport: new DefaultChatTransport({
      api: `${appConfig.sevenbridges.url}/api/chat/${AgentId.conversationalAgent}`,
      fetch: fetchClient,
      prepareSendMessagesRequest: async (request: any) => ({
        body: {
          messages: [request.messages.at(-1)],
          entityId,
          entityVariant,
        },
      }),
    }),
  });

  // Using satisfies to make type documentation easier to access, no functional requirement
  const status = getConversationStatus(loading, chatSdkStatus, messages) satisfies ConversationStatus;

  const isProcessingMessage = status === 'streaming' || status === 'submitted';
  const isDisabled = isNil(conversationId) || (status !== 'ready' && status !== 'error' && status !== 'initialized');

  // We only want to run this effect when there are initial messages and the
  // messages from the chat SDK are empty yet, meaning we load the initial state from backend
  // Levering `useChat({ messages: messageHistory })` doesn't work as the initial messages are always empty
  // and setting the react-like internal state requires the useEffect hook for parameters passed in later
  // See e.g. https://github.com/vercel/ai/issues/9914 or https://github.com/vercel/ai/discussions/7112
  useChatHistoryHydration({
    sessionKey: `${conversationId ?? 'no-conversation'}:${internalStreamId}`,
    enabled: true,
    history: messageHistory,
    messages,
    setMessages,
  });

  return {
    status,
    messages,
    error,
    sendMessage,
    resetStream: () => {
      setInternalStreamId(uuidV4());
    },
    inputProps: {
      disabled: isDisabled,
      loading: isProcessingMessage,
    },
  };
};
